<script setup lang="ts">
import { computedAsync } from '@vueuse/core';

import {
  useBrowserStore,
  useCoreStore,
} from '@apps/main/new-desktop/core/stores';

import {
  AppImagesLoader,
  AppLazyLoadComponent,
  AppSvgIcon,
  AppTag,
} from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import DateCardTag from '../DateCardTag.vue';
import type { FlightProductCardData } from '../../../models';

interface FlightProductCardContentProps {
  productCardContent: FlightProductCardData;
}

const props = defineProps<FlightProductCardContentProps>();

interface FlightProductCardContentEmits {
  (eventName: 'show-details'): void;
}

const emit = defineEmits<FlightProductCardContentEmits>();

const browserStore = useBrowserStore();
const coreStore = useCoreStore();

const isHideOverviewTimelineExperiment = computedAsync<boolean>(
  () => isExperimentAsync('hide_overview_timeline'),
  false
);

function onContentClick(): void {
  if (browserStore.isSmallerThan1200px) {
    emit('show-details');
  }
}
</script>

<template>
  <button class="flight-product-card-content" @click="onContentClick">
    <div class="flight-content">
      <div class="top-section">
        <div
          v-if="props.productCardContent.details.carrier"
          class="carrier-data"
        >
          <AppLazyLoadComponent class="logo">
            <AppImagesLoader
              :key="
                props.productCardContent.details.carrier.logo.urls.join('-')
              "
              :urls="props.productCardContent.details.carrier.logo.urls"
              alt="flight carrier logo"
              img-type="scaleX"
              @images-loader-error="coreStore.handleImageLoadError"
            />
          </AppLazyLoadComponent>

          <div class="name">
            {{ props.productCardContent.details.carrier.name }}
          </div>
        </div>
        <div class="destinations">
          {{ props.productCardContent.fromPort.name }}
          &mdash;
          {{ props.productCardContent.toPort.name }}
        </div>
      </div>
      <div class="bottom-section">
        <div class="details">
          <div class="origin">
            {{ props.productCardContent.fromPort.time }}
            <div class="airport">
              <span class="desktop-port-name">
                {{ props.productCardContent.fromPort.name }}
              </span>
              <span>
                {{
                  browserStore.isSmallerThan1200px
                    ? props.productCardContent.fromPort.code
                    : `(${props.productCardContent.fromPort.code})`
                }}
              </span>
            </div>
          </div>

          <div class="time">
            <div class="duration">
              {{ props.productCardContent.details.time }}
            </div>
            <div class="separator">
              <div class="line" />
              <div class="icon-wrapper">
                <AppSvgIcon name="airplane" size="13" color="color-white" />
              </div>
            </div>
            <div class="stops">
              {{ props.productCardContent.details.mainDetails }}
            </div>
          </div>

          <div class="destination">
            {{ props.productCardContent.toPort.time }}
            <div class="airport">
              <span class="desktop-port-name">
                {{ props.productCardContent.toPort.name }}
              </span>
              <span>
                {{
                  browserStore.isSmallerThan1200px
                    ? props.productCardContent.toPort.code
                    : `(${props.productCardContent.toPort.code})`
                }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="props.productCardContent.details.warningTags.length"
          class="warning-tags"
        >
          <AppTag
            v-for="(tag, index) in props.productCardContent.details.warningTags"
            :key="index"
            type="warning"
            :icon="{ name: tag.svgIconType, size: '13' }"
            class="tag-container"
          >
            <template #title>
              <span class="tag-description">
                {{ $t(tag.description) }}
              </span>
            </template>
          </AppTag>
        </div>
      </div>
      <DateCardTag
        v-if="isHideOverviewTimelineExperiment"
        :date="props.productCardContent.details.date"
      />
    </div>
  </button>
</template>

<style scoped lang="scss">
.flight-product-card-content {
  all: unset;
  width: 100%;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: auto;

  @include for-less-or-equal-than-599-px {
    grid-template-rows: auto auto;
    grid-template-columns: auto;
  }

  .warning-tags {
    margin-top: var(--spacing-12);
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-4);
    justify-content: center;

    .tag-description {
      @include font-label-s-light;
    }
  }

  .flight-content {
    --icon-size: 30px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: var(--spacing-12);
    height: fit-content;
    border-right: none;
    flex-wrap: wrap;
    position: relative;

    .top-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-8);

      .carrier-data {
        display: flex;
        align-items: center;
        gap: var(--spacing-8);
        @include font-body-s-light;

        .logo {
          height: var(--icon-size);
          width: auto;
        }

        .name {
          width: 100%;
          text-align: center;

          @include for-more-or-equal-than-599-px {
            text-align: start;
          }
        }
      }

      .destinations {
        display: block;
        @include font-label-s-light;

        @include for-more-or-equal-than-599-px {
          display: none;
        }
      }
    }
    .bottom-section {
      flex-grow: 1;
      padding: var(--spacing-16) 0 0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      @include for-more-or-equal-than-599-px {
        padding: 0 0 var(--icon-size) 0;
      }

      .details {
        --mid-block-width: 100px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-16);
        width: 100%;
        .origin,
        .destination {
          display: flex;
          flex-direction: column;
          // align origin and destination columns mid block width
          flex-basis: calc(50%);
          gap: var(--spacing-4);
          @include font-label-xxl-bold;
          span {
            @include font-label-l-light;
          }

          .airport {
            color: var(--color-secondary-60);
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: var(--spacing-4);
            .desktop-port-name {
              display: none;
              @include for-more-or-equal-than-599-px {
                display: block;
              }
            }
          }
        }
        .origin {
          align-items: flex-end;
          .desktop-port-name {
            text-align: end;
          }
          .airport {
            justify-content: end;
          }
        }
        .destination {
          align-items: flex-start;
        }

        .time {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--spacing-12);
          width: 100px;

          .duration {
            @include font-label-s-light;
            color: var(--color-secondary-60);
          }
          .separator {
            position: relative;
            width: 100%;
            height: var(--spacing-24);

            .line {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 100%;
              height: 1px;
              background: rgb(255, 255, 255);
              background: linear-gradient(
                90deg,
                rgba(255, 255, 255, 1) 0%,
                rgba(36, 30, 61, 1) 50%,
                rgba(255, 255, 255, 1) 100%
              );
            }
            .icon-wrapper {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              background: var(--color-primary-5);
              border-radius: var(--border-radius-20);
              padding: var(--spacing-4);
            }
          }
          .stops {
            color: var(--color-secondary-60);
            @include font-label-s-light;
            text-align: center;
            white-space: nowrap;
          }
        }
      }
    }
  }
}
</style>
